import { content } from "./content";
import { BsGithub } from "react-icons/bs";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { SectionWrapper } from "./hoc";
import { styles } from "./styles";
import { textVariant, fadeIn } from "./utils/motion";

/* ---------------- MINI CARD ---------------- */

const ProjectMiniCard = ({ project, isActive, onClick }) => (
  <motion.div
    onClick={onClick}
    variants={fadeIn("up", "spring", 0.1, 0.75)}
    className={`about-bg rounded-2xl shadow-card p-4 cursor-pointer
      transition-all duration-300
      ${isActive ? "ring-2 ring-violet-500" : "hover:scale-[1.03]"}`}
  >
    <img
      src={project.image}
      alt={project.title}
      className="rounded-xl w-full h-32 object-cover"
    />

    <h4 className="mt-3 text-sm font-semibold text-white line-clamp-2">
      {project.title}
    </h4>
  </motion.div>
);

/* ---------------- HERO ---------------- */

const ProjectHero = ({ project, onPrev, onNext, hasPrev, hasNext }) => {
  if (!project) return null; // ✅ HARD GUARD

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="about-bg rounded-3xl shadow-card p-8"
    >
      <img
        src={project.image}
        alt={project.title}
        className="rounded-2xl w-full h-72 object-cover"
      />

      <h2 className="mt-6 text-2xl font-bold text-white">
        {project.title}
      </h2>

      <p className="mt-4 text-gray-300">
        {project.overview}
      </p>

      <h4 className="mt-6 font-semibold text-white">What I Built</h4>
      <ul className="mt-2 space-y-1 text-gray-300 text-sm">
        {project.built.map((item, i) => (
          <li key={i}>• {item}</li>
        ))}
      </ul>

      <h4 className="mt-6 font-semibold text-white">Why It Matters</h4>
      <p className="mt-2 text-gray-300 text-sm">
        {project.why}
      </p>

      <div className="flex flex-wrap gap-2 mt-6">
        {project.tech.map((t, i) => (
          <span
            key={i}
            className="px-3 py-1 text-xs rounded-full green-pink-gradient text-white"
          >
            {t}
          </span>
        ))}
      </div>

      {/* ACTIONS */}
      <div className="flex items-center justify-between mt-10">
        <a
          href={project.github}
          target="_blank"
          rel="noreferrer"
          className="text-3xl text-white hover:text-[#915EFF]"
        >
          <BsGithub />
        </a>

        <div className="flex gap-4">
          <button
            onClick={onPrev}
            disabled={!hasPrev}
            className={`px-5 py-2 rounded-xl border
              ${hasPrev
                ? "border-violet-400 text-violet-400 hover:bg-violet-400 hover:text-black"
                : "border-gray-600 text-gray-600 cursor-not-allowed"
              }`}
          >
            Prev
          </button>

          <button
            onClick={onNext}
            disabled={!hasNext}
            className={`px-5 py-2 rounded-xl border
              ${hasNext
                ? "border-violet-400 text-violet-400 hover:bg-violet-400 hover:text-black"
                : "border-gray-600 text-gray-600 cursor-not-allowed"
              }`}
          >
            Next
          </button>
        </div>
      </div>
    </motion.div>
  );
};

/* ---------------- MAIN ---------------- */

const Projects = () => {
  const { projects = [] } = content;

  // ✅ Safe state
  const [activeIndex, setActiveIndex] = useState(0);

  // ✅ Guard if projects load late
  useEffect(() => {
    if (projects.length > 0 && activeIndex >= projects.length) {
      setActiveIndex(0);
    }
  }, [projects, activeIndex]);

  if (!projects.length) return null;

  const activeProject = projects[activeIndex];

  return (
    <>
      {/* Header */}
      <motion.div variants={textVariant()} className="text-center">
        <p className={styles.sectionSubText}>My Work</p>
        <h2 className={styles.sectionHeadText}>
          Projects<span className="text-[#915EFF]">.</span>
        </h2>
      </motion.div>

      <div className="mt-16 max-w-7xl mx-auto px-6 space-y-14">

        {/* MINI CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {projects.map((p, i) => (
            <ProjectMiniCard
              key={p.id}
              project={p}
              isActive={i === activeIndex}
              onClick={() => setActiveIndex(i)}
            />
          ))}
        </div>

        {/* HERO */}
        <ProjectHero
          project={activeProject}
          onPrev={() => setActiveIndex((i) => Math.max(i - 1, 0))}
          onNext={() => setActiveIndex((i) => Math.min(i + 1, projects.length - 1))}
          hasPrev={activeIndex > 0}
          hasNext={activeIndex < projects.length - 1}
        />

      </div>
    </>
  );
};

export default SectionWrapper(Projects, "projects");
